import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {AfterViewInit, Component, forwardRef, Input, ViewChild} from '@angular/core';
import {TitleKey} from '../../../api/models/title-key';
import {TextSearchValue} from '../text-search-filter/text-search-filter.component';
import {JobDepartment} from '../../../api/models/job-department';
import {JobSeniority} from '../../../api/models/job-seniority';
import {Title} from '../../../api/models/title';
import {CacheDataService} from '../../../api/services/cache-data.service';
import {TitleSearchComponent} from '../title-search.component';
import {JobSenioritySearchComponent} from '../job-seniority-search.component';
import {JobDepartmentSearchComponent} from '../job-department-search.component';
import * as _ from 'lodash';

@Component({
  selector: 'full-title-search',
  templateUrl: './full-title-search.component.html',
  styleUrls: ['./full-title-search.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FullTitleSearchComponent),
      multi: true,
    },
  ],
})
export class FullTitleSearchComponent implements ControlValueAccessor, AfterViewInit {

  @ViewChild(JobSenioritySearchComponent, {static: false}) senioritySearchComponent: JobSenioritySearchComponent;
  @ViewChild(JobDepartmentSearchComponent, {static: false}) departmentSearchComponent: JobDepartmentSearchComponent;
  @ViewChild(TitleSearchComponent, {static: false}) titleSearchComponent: TitleSearchComponent;
  @Input() fixedHeight: number = 500;

  constructor(private cacheDataService: CacheDataService) {

  }

  public jobDepartmentSelectedValues: TextSearchValue<JobDepartment> = { searchTerms: [], selectedValues: [] }
  public jobSenioritySelectedValues: TextSearchValue<JobSeniority> = { searchTerms: [], selectedValues: [] }
  public titleSelectedValues: TextSearchValue<Title> = { searchTerms: [], selectedValues: [] }

  private _onChangedCallback: (value: TitleKey[]) => void = () => {};
  private _onTouchedCallback: () => void = () => {};

  ngAfterViewInit(): void {
  }

  public async search(titleNames: string[], pageNumber: number, pageSize: number, complete: (totalPages: number, titles: Title[]) => void) : Promise<{totalPages: number, titles: Title[]}> {
    const departments = [...this.jobDepartmentSelectedValues?.searchTerms ?? [], ...this.jobDepartmentSelectedValues?.selectedValues.map(x => x.department) ?? []];
    const seniorities = [...this.jobSenioritySelectedValues?.searchTerms ?? [], ...this.jobSenioritySelectedValues?.selectedValues.map(x => x.seniority) ?? []];

    const result = await this.cacheDataService.cacheDataSearchTitles({pageNumber, pageSize, departmentsSearch: departments, senioritiesSearch: seniorities, titlesSearch: titleNames}).toPromise();

    this._onChangedCallback(this.getValue());

    return {
      totalPages: result.totalPages,
      titles: result.page
    };
  }

  get value(): any {
    return this.getValue();
  }

  set value(v: any) {
    this.setValue(v);
    this._onChangedCallback(v);
    this.dataChanged();
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this._onChangedCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Implement if needed
  }

  public setValue(titleKeys: TitleKey[]) {
    if (titleKeys) {
      let titleSearches: string[] = titleKeys.filter(x => x.titleSearch).map(x => x.titleSearch);
      let titles: Title[] = titleKeys.filter(x => x.key).map<Title>(x => {
        return {
          key: x.key,
          name: x.displayName,
          departmentId: 0,
          seniorityId: 0,
          socScore: 0,
          adjustedSocScore: 0
        }
      });
      let departmentSearches: string[] = titleKeys.filter(x => x.departmentSearch).map(x => x.departmentSearch);
      let jobDepartments: JobDepartment[] = titleKeys.filter(x => x.departmentId).map<JobDepartment>(x => {
        return {
          departmentId: x.departmentId
        }
      });
      let senioritySearches: string[] = titleKeys.filter(x => x.senioritySearch).map(x => x.senioritySearch);
      let jobSeniorities: JobSeniority[] = titleKeys.filter(x => x.seniorityId).map<JobSeniority>(x => {
        return {
          seniorityId: x.seniorityId
        }
      });

      const newSeniority = {searchTerms: senioritySearches, selectedValues: jobSeniorities};
      if (!_.isEqual(this.jobSenioritySelectedValues, newSeniority)) {
        this.jobSenioritySelectedValues = newSeniority;
      }

      const newDepartment = {searchTerms: departmentSearches, selectedValues: jobDepartments};
      if (!_.isEqual(this.jobDepartmentSelectedValues, newDepartment)) {
        this.jobDepartmentSelectedValues = newDepartment;
      }

      const newTitles = {searchTerms: titleSearches, selectedValues: titles};
      if (!_.isEqual(this.titleSelectedValues, newTitles)) {
        this.titleSelectedValues = newTitles;
      }
    }
  }

  getValue() : TitleKey[] {
    let newValues : TitleKey[] = [];
    this.titleSelectedValues?.searchTerms.forEach((x: string) => { newValues.push({ titleSearch: x }); });
    this.titleSelectedValues?.selectedValues.forEach((x: Title) => { newValues.push({ key: x.key, displayName: x.name }); });
    this.jobDepartmentSelectedValues?.searchTerms.forEach((x: string) => { newValues.push({ departmentSearch: x }); });
    this.jobDepartmentSelectedValues?.selectedValues.forEach((x: JobDepartment) => { newValues.push({ departmentId: x.departmentId }); });
    this.jobSenioritySelectedValues?.searchTerms.forEach((x: string) => { newValues.push({ senioritySearch: x }); });
    this.jobSenioritySelectedValues?.selectedValues.forEach((x: JobSeniority) => { newValues.push({ seniorityId: x.seniorityId }); });
    return newValues;
  }

  public dataChanged(shouldRefreshTitles: boolean = false) : void {
    this.titleSearchComponent?.dataUpdated(shouldRefreshTitles);
  }

  refreshStep: number = -1;

  public forceRefresh() {
    this.refreshStep = 0;
    this.senioritySearchComponent.forceRefresh();
  }

  dataLoadComplete(source: string) {
    console.log('Data Load Complete: ' + source);

    if (source === 'seniority' && this.refreshStep === 0) {
      this.refreshStep = 1;
      this.departmentSearchComponent.forceRefresh();
    } else if (source === 'department' && this.refreshStep === 1) {
      this.refreshStep = 2;
      this.titleSearchComponent.forceRefresh();
    } else if (source === 'title' && this.refreshStep === 2) {
      this.refreshStep = -1;
    }
  }
}
